import React, { useState, useEffect } from "react";
import { fetchUserDetails } from "services/userService";
import TournamentRegistrationForm from "Components/Registration/TournamentRegistrationForm";
import TournamentParticipantsList from "./TournamentParticipantList";
import {
  canManage,
  toggleRegistrationClosed,
  subscribeToTournamentRegistrations,
  archiveTournament,
} from "services/tournamentService";
import RegistrationLimit from "./RegistrationLimit";
import { Lock, Unlock, Pencil, Archive } from "lucide-react";
import TournamentPodium from "./tournamentPodium";
import TeamsTables from "./TeamsTables";
import { Link } from "react-router-dom";
import TournamentTeamManager from "./TourmanentTeamManager";
import * as Sentry from "@sentry/react";

const Tournament = ({
  tournament,
  user,
  users = [],
  locationRankings = [],
}) => {
  const [userDetails, setUserDetails] = useState({});
  const [registrationClosed, setRegistrationClosed] = useState(
    tournament.registrationClosed
  );
  const [registrations, setRegistrations] = useState([]);

  const isAdmin = canManage(user, tournament.locationId);

  useEffect(() => {
    const unsubscribe = subscribeToTournamentRegistrations(
      tournament.id,
      setRegistrations
    );
    return () => unsubscribe();
  }, [tournament.id]);

  useEffect(() => {
    const loadUserDetails = async () => {
      if (!isAdmin || registrations.length === 0) return;
      const userIds = registrations.map((reg) => reg.userId);
      const userData = await fetchUserDetails(userIds);
      setUserDetails((prev) => ({ ...prev, ...userData }));
    };
    loadUserDetails();
  }, [registrations, tournament.id, isAdmin]);

  const handleToggleRegistration = async () => {
    try {
      const newStatus = await toggleRegistrationClosed(
        tournament.id,
        registrationClosed
      );
      setRegistrationClosed(newStatus);
    } catch (error) {
      Sentry.captureException(error);
      alert("Une erreur s'est produite en changeant le statut d'inscription.");
    }
  };

  const handleArchive = async () => {
    if (window.confirm("Archiver ce tournoi ?")) {
      try {
        await archiveTournament(tournament.id);
        alert("Tournoi archivé.");
      } catch (error) {
        Sentry.captureException(error);
        console.error(error);
        alert("Erreur lors de l'archivage.");
      }
    }
  };

  const handleEdit = () => {
    window.location.href = `/locations/tournaments/edit/${tournament.id}`;
  };

  return (
    <div className="flex flex-col w-full max-w-screen-2xl mx-auto bg-gray-700 rounded-xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="flex flex-wrap gap-2 justify-between items-center p-2 bg-sky-900 border-b-2 border-gray-500">
        <h3 className="text-xl font-semibold text-gray-300 flex flex-row items-end gap-2 ml-2">
          {tournament.type && tournament.date ? (
            <>
              {tournament.type} —{" "}
              {new Date(tournament.date).toLocaleDateString("fr-FR", {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
              <span className="text-amber-400">
                {tournament.time && ` à ${tournament.time}`}
              </span>
              
            </>
          ) : (
            tournament.name
          )}
        </h3>
        {isAdmin && (
          <div className="flex space-x-2">
            <button
              onClick={handleEdit}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-md"
            >
              <Pencil size={16} /> Modifier
            </button>
            <button
              onClick={handleArchive}
              className="flex items-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-500 text-white rounded-md"
            >
              <Archive size={16} /> Archiver
            </button>
          </div>
        )}
      </div>

      {isAdmin && <TournamentPodium tournament={tournament} />}

      <section className="flex flex-col md:flex-row gap-4 p-4">
        {/* Left Column: Image & Teams */}
        <div className="w-full md:w-1/2 flex flex-col gap-4">
          <div className="rounded-lg overflow-hidden shadow-xl">
            <img
              src={tournament.image || "https://via.placeholder.com/600"}
              alt={tournament.name}
              className="w-full h-52 md:h-72 object-cover"
            />
          </div>

          {isAdmin && (
            <div className="bg-gray-600 rounded-lg p-4 shadow-xl">
              <h4 className="text-xl font-semibold text-white mb-2">
                👥 Gestion des équipes
              </h4>
              <TournamentTeamManager
                tournament={tournament}
                locationId={tournament.locationId}
                registrations={registrations}
                locationRankings={locationRankings}
              />
            </div>
          )}

          {Object.keys(tournament.teams || {}).length > 0 && isAdmin && (
            <div className="bg-gray-600 rounded-lg p-4 shadow-xl">
              <h4 className="text-xl font-semibold text-white mb-2">
                🏅 Équipes du tournoi
              </h4>
              <TeamsTables teams={tournament.teams} />
              <Link
                to={`/locations/tournaments/${tournament.id}/progress`}
                className="text-white hover:underline"
              >
                Accéder au tableau des matchs
              </Link>
            </div>
          )}
        </div>

        {/* Right Column: Controls & Participants */}
        <div className="w-full md:w-1/2 flex flex-col gap-4">
          {isAdmin && (
            <div className="flex items-center gap-3 text-white">
              <input
                type="checkbox"
                checked={!registrationClosed}
                onChange={handleToggleRegistration}
                className="w-5 h-5 accent-yellow-500"
              />
              {registrationClosed ? <Lock size={20} /> : <Unlock size={20} />}
              <span>
                {registrationClosed
                  ? "Inscriptions fermées"
                  : "Inscriptions ouvertes"}
              </span>
            </div>
          )}

          {isAdmin && <RegistrationLimit tournament={tournament} />}
          <div className="bg-gray-800 rounded-lg p-4 shadow-xl">
            <div className="flex flex-row">
              <h4 className="text-lg font-semibold text-white mb-2">
                👥 Inscriptions
              </h4>
              <h4 className="ml-2 text-lg font-semibold text-green-500 mb-2">
                - maximum de {tournament.waitingListLimit}
              </h4>
            </div>

            <TournamentRegistrationForm
              tournament={tournament}
              user={user}
              registrations={registrations}
              prefetchedUsers={users}
            />
            <TournamentParticipantsList
              tournament={tournament}
              user={user}
              registrations={registrations}
              userDetails={userDetails}
              locationRankings={locationRankings}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tournament;
